/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: lazy-load the below-fold A+ renderer to keep it out of the initial PDP chunk.
import { useState, Suspense, Fragment, lazy } from 'react';
import { Await, useRouteLoaderData, type MetaDescriptor } from 'react-router';
import type { loader as rootLoader } from '@/root';
import { shouldRevalidate as shouldRevalidateProduct } from '@/lib/revalidation/routes/product';
import type { Route } from './+types/_app.product.$productId';
import { type ShopperProducts } from '@/scapi';
import { fetchProductById } from '@/lib/api/products.server';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { siteContext, stripPathPrefix } from '@salesforce/storefront-next-runtime/site-context';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
// RGBARRY-CUSTOMIZATION: 301-canonicalize the native id-based PDP URL to its production SEO slug (see loader).
import { canonicalizeProductId } from '@/lib/product/canonicalize-product.server';
import ProductView from '@/components/product-view';
import ChildProducts from '@/components/product-view/child-products';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import { CategoryHrefProvider, type CategoryUrlMap } from '@/components/navigation-menu/category-href';
import { collectProductBreadcrumbCategoryUrls } from '@/lib/product/seo-url';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
// RGBARRY-CUSTOMIZATION: server-defer the below-the-fold PDP recommendation carousels instead of
// client-fetching them post-hydration (removes a post-load fetch + main-thread work → lower TBT).
import DeferredProductRecommendations from '@/components/product-recommendations/deferred';
import { ProductRecommendationSkeleton } from '@/components/product/skeletons';
import { fetchProductRecommendations } from '@/lib/product/recommendations.server';
import type { Recommendation } from '@/hooks/recommenders/use-recommenders';
import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
import { useTranslation } from 'react-i18next';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
// RGBARRY-CUSTOMIZATION: emit one product-view event across master-to-variant PDP resolution.
import { useProductViewTracking } from '@/hooks/product/use-product-view-tracking';
import { Region } from '@/components/region';
import { ProductProvider } from '@/providers/product-context';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { generateProductSchema } from '@/utils/product-schema';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { buildProductSeoDescription } from '@/utils/seo-description';
// RGBARRY-CUSTOMIZATION (W-23754187): emit the production-format PDP canonical (`/{slug}/{id}.html`)
// instead of the native `/product/{id}` request path — see the `seoMeta` computation in the loader.
import { buildSeoMetaDescriptors } from '@/utils/seo';
import { getCanonicalProductPath } from '@/lib/product/canonical-product-url';
import { getAppOrigin } from '@/lib/origin';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { getLogger } from '@/lib/logger.server';
import { cn } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
// RGBARRY-CUSTOMIZATION: render recognized legacy A+ carousel assets responsively without executing authored JS.
import { fetchContentAssetHtml } from '@/lib/api/content-asset.server';
import type { APlusContentAsset } from '@/components/a-plus-content';
// END RGBARRY-CUSTOMIZATION
// RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
import { getFibblConfig, type FibblConfig } from '@/lib/fibbl.server';
// END RGBARRY-CUSTOMIZATION
// RGBARRY-CUSTOMIZATION: PDP color swatch grouping by sale status
import { getIsSaleByColorValue } from '@/lib/product/product-sale.server';
// END RGBARRY-CUSTOMIZATION
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { selectedStoreContext } from '@/extensions/store-locator/middlewares/selected-store.server';
// RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP — import disabled with its usage below
// import PickupProvider from '@/extensions/bopis/context/pickup-context';
// @sfdc-extension-block-end SFDC_EXT_BOPIS
// @sfdc-extension-block-start SFDC_EXT_BNPL
import {
    getBuyNowPayLaterMessage,
    getBuyNowPayLaterLearnMore,
    type BuyNowPayLaterMessageData,
    type BuyNowPayLaterLearnMoreData,
} from '@/extensions/bnpl/lib/api/bnpl.server';
import { BnplProvider } from '@/extensions/bnpl/context/bnpl-context';
// @sfdc-extension-block-end SFDC_EXT_BNPL
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import {
    getWriteReviewForm,
    type ReviewsSummaryData,
    type ReviewsData,
    type WriteReviewFormData,
} from '@/extensions/ratings-reviews/lib/api/reviews.server';
import { buildBvReviewsSummary } from '@/extensions/ratings-reviews/lib/bv-catalog-summary';
import { ProductReviewsProvider } from '@/extensions/ratings-reviews/providers/product-reviews-context';
import { WriteReviewFormProvider } from '@/extensions/ratings-reviews/context/write-review-form-context';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
// @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
import {
    getReturnsAndWarranty,
    pdpSectionApi,
    type ReturnsAndWarrantyData,
    type HtmlContent,
} from '@/extensions/product-content/lib/api/product-content.server';
import { resolvePdpSections } from '@/extensions/product-content/lib/pdp-sections';
import { ProductContentDataProvider } from '@/extensions/product-content/context/product-content-data-context';
// @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
// @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
import {
    getEstimatedDelivery,
    type EstimatedDeliveryData,
} from '@/extensions/shipping-delivery/lib/api/shipping-delivery.server';
import { ShippingDeliveryProvider } from '@/extensions/shipping-delivery/context/shipping-delivery-context';
// @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

// RGBARRY-CUSTOMIZATION: keep the below-fold, asset-specific A+ renderer out of the initial PDP chunk.
const APlusContent = lazy(() => import('@/components/a-plus-content'));

@PageType({
    name: 'Product Detail Page',
    description: 'Product detail page with product information, images, and recommendations',
    supportedAspectTypes: ['pdp'],
})
@RegionDefinition([
    {
        id: 'promoContent',
        name: 'Promo Content Region',
        description: 'Promotional content region above main product content',
        maxComponents: 1,
    },
    {
        id: 'engagementContent',
        name: 'Engagement Content Region',
        description: 'Engagement content region for recommendations and related products below main content',
        maxComponents: 1,
    },
])
export class ProductPageMetadata {}

export type ProductPageData = {
    product: ShopperProducts.schemas['Product'];
    // RGBARRY-CUSTOMIZATION (W-23875749): categoryId -> production SEO slug path for the breadcrumb
    // chain. Read SYNCHRONOUSLY off the enriched product response — the product hook stamps `c_seoUrl`
    // onto each node of the embedded `primaryCategory` chain the loader already expands — so breadcrumb
    // links carry prod slugs from the SINGLE product GET with NO extra category fetch (Kevin He review,
    // PR #107). Empty when there's no primary category or the product wasn't enriched (each crumb then
    // falls back to /category/:id).
    categoryUrls: CategoryUrlMap;
    page: ReturnType<typeof fetchPageWithComponentData>;
    pageKey: string;
    pageUrl: string;
    // RGBARRY-CUSTOMIZATION (production SEO audit): bounded plain-text description, constructed in
    // the loader with the request-localized site name so it does not perform HTML work during render.
    seoDescription?: string;
    // RGBARRY-CUSTOMIZATION (W-23754187): canonical + hreflang descriptors emitted by this route's
    // `meta` export, overriding root's default canonical so the PDP canonical points at production's
    // `/{slug}/{id}.html` form rather than the native `/product/{id}` request path.
    seoMeta: MetaDescriptor[];
    productSchema: Promise<ReturnType<typeof generateProductSchema> | null>;
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    bnplMessage: Promise<BuyNowPayLaterMessageData>;
    bnplLearnMore: Promise<BuyNowPayLaterLearnMoreData>;
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData> | null;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarranty: Promise<ReturnsAndWarrantyData>;
    pdpCollapsibles: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    estimatedDelivery: Promise<EstimatedDeliveryData>;
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
    // RGBARRY-CUSTOMIZATION: retain the sanitized HTML plus optional structured A+ model for rendering.
    aPlusContent: Promise<APlusContentAsset | null>;
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig: FibblConfig;
    // RGBARRY-CUSTOMIZATION: below-the-fold Einstein carousels, server-deferred (unresolved promises,
    // never blocking the loader) — replaces the previous client-side useRecommenders fetch.
    pdpCompleteSetRecs: Promise<Recommendation>;
    pdpRecentlyViewedRecs: Promise<Recommendation>;
    // END RGBARRY-CUSTOMIZATION
    // RGBARRY-CUSTOMIZATION: PDP color swatch grouping by sale status — awaited (not deferred) so the
    // swatch rows never shift after the initial render, per product decision.
    isSaleByColorValue: Record<string, boolean>;
    // END RGBARRY-CUSTOMIZATION
};

/**
 * Server-side loader function that fetches product data.
 * This function runs on the server during SSR and can access cookies for store information.
 *
 * The product is awaited as critical data: a 404 from SCAPI is re-thrown as
 * `Response(message, { status: 404 })` so React Router renders the 404 page with
 * the proper HTTP status (essential for SEO).
 *
 * @returns Object containing the resolved product, page data, and schema promises
 */
export async function loader(args: Route.LoaderArgs): Promise<ProductPageData> {
    const { request, params, context } = args;
    const logger = getLogger(context);
    const { productId } = params;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const variantPid = searchParams.get('pid');
    logger.debug('Product: loader starting', {
        productId,
        variantPid: variantPid || undefined,
    });

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = context.get(selectedStoreContext);
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Get currency from context for product pricing
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Product: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;

    // Resolve the product critically. A 404 here must propagate as Response(404)
    // so the route error boundary renders the 404 page with the correct HTTP status.
    const productLookupId = variantPid || productId;

    let product: ShopperProducts.schemas['Product'] | null;
    try {
        product = await fetchProductById(context, productLookupId, {
            expand: [
                'availability', // <-- TTL = 60s (!)
                'bundled_products',
                'images',
                'options',
                'page_meta_tags',
                'prices', // <-- TTL = 900s
                'primary_category',
                'promotions', // <-- TTL = 900s
                'set_products',
                'variations',
            ],
            allImages: true,
            perPricebook: true,
            ...(currency ? { currency } : {}),
            // @sfdc-extension-block-start SFDC_EXT_BOPIS
            // Include inventoryIds parameter when store is selected
            ...(selectedStoreInfo?.inventoryId ? { inventoryIds: [selectedStoreInfo.inventoryId] } : {}),
            // @sfdc-extension-block-end SFDC_EXT_BOPIS
        });
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    if (!product) {
        throw new Response('Product not found', { status: 404 });
    }

    // RGBARRY-CUSTOMIZATION: group color swatches from c_isSale booleans injected into this product's
    // embedded variants by the product-response hook; no per-color SCAPI requests are required.
    const isSaleByColorValue = getIsSaleByColorValue(product);

    // RGBARRY-CUSTOMIZATION: look up a variant's master up front, in parallel with the work below. SCAPI keeps
    // serving an online variant whose master is offline, so a discontinued variant rendered as an unsellable page
    // and its deferred A+ fallback (variants inherit the master's c_aPlusContentAssetID) failed after the shell,
    // which answered crawlers with a 500. The lookup never rejects: a 404 marks the master missing, any other
    // failure just leaves it unknown.
    const masterId = product.master?.masterId;
    const masterLookup: Promise<{ master: ShopperProducts.schemas['Product'] | null; missing: boolean }> = masterId
        ? fetchProductById(context, masterId, { ...(currency ? { currency } : {}) }).then(
              (master) => ({ master, missing: !master }),
              (error: unknown) => ({
                  master: null,
                  missing: error instanceof NormalizedApiError && error.status === 404,
              })
          )
        : Promise.resolve({ master: null, missing: false });

    // RGBARRY-CUSTOMIZATION (W-23754187): resolve the public origin ONCE and reuse it for both the
    // `og:url`/JSON-LD `url` (`pageUrl`) and the canonical/hreflang links (`seoMeta`), so the two can
    // never diverge — not even in the degraded no-forwarded-header fallback. `getAppOrigin` reads the
    // same public forwarding headers as `getPublicOrigin` but falls back to `EXTERNAL_DOMAIN_NAME`
    // instead of `request.url.origin`, so the internal Lambda/CDN origin can never leak into any tag.
    const publicOrigin = getAppOrigin(context);

    // RGBARRY-CUSTOMIZATION (W-23754187): resolve the production-format PDP path (`/{slug}/{id}.html`)
    // from the product's SCAPI `slugUrl`. This route serves BOTH the native `/product/{id}` URL and
    // (via delegation) the legacy `/{slug}/{id}.html` URL. `getCanonicalProductPath` parses the slug
    // out of `slugUrl` and rebuilds `/{slug}/{productId}.html` using the master id from the REQUEST
    // path (so a `?pid=` variant request still canonicalizes to the master URL, query dropped). It
    // returns null when the slug can't be determined, in which case we fall back to the request path.
    const canonicalProductPath = getCanonicalProductPath(product.slugUrl, productId);

    // RGBARRY-CUSTOMIZATION: canonical redirect for the native id-based PDP URL. SFRA prod 301s any
    // non-canonical PDP form to `/{slug}/{id}.html`; PR #141 (W-23754187) added only the
    // `<link rel="canonical">` hint, so the native `/product/:id` URL still served a crawlable
    // duplicate in place. Fire the hard 301 here — after the product fetch (its slug is only known
    // post-fetch) but BEFORE the deferred schema/recommendations/PD work below, so an id hit does no
    // wasted data work. Query is preserved so a `?pid=<variant>` selection survives the redirect.
    // GATES:
    //   - only on the NATIVE `/product/:id` form. The dedicated `/:slug/:productId.html` route and the
    //     decorated-PDP catch-all serve the `.html` forms; their request paths don't start with
    //     `/product/`, so this is false there and the PDP renders in place (no redirect loop).
    //   - never in Page Designer edit/preview: the PD editor drives the `/product/:id?mode=…&pageId=…
    //     &pdToken=…` URL directly, so redirecting to the slug would break the editing session.
    const pageDesignerModeActive = isDesignModeActive(request) || isPreviewModeActive(request);
    const strippedPath = stripPathPrefix({
        pathname: requestUrl.pathname,
        prefix: getConfig(context).url?.prefix ?? '',
    });
    if (!pageDesignerModeActive && /^\/product\//i.test(strippedPath)) {
        // Reuse the `canonicalProductPath` already derived above — canonicalizeProductId no longer
        // re-parses `slugUrl`, so the canonical path is computed exactly once on the PDP loader path.
        const productRedirect = canonicalizeProductId(context, canonicalProductPath, productId, requestUrl.search);
        if (productRedirect) throw productRedirect;
    }

    // RGBARRY-CUSTOMIZATION: a variant whose master is gone and which cannot be ordered is a discontinued product;
    // answer 404 before any deferred work starts, so the page drops out of search like the master URL already does.
    // A variant that is still orderable keeps its page (prod has such variants with stock left under an offline
    // master); it simply renders without the master's A+ content.
    const { master: masterProduct, missing: masterMissing } = await masterLookup;
    if (masterMissing && !product.inventory?.orderable) {
        logger.warn('Product: master product not found and variant not orderable, returning 404', {
            productId: productLookupId,
            masterId,
        });
        throw new Response('Product not found', { status: 404 });
    }

    // RGBARRY-CUSTOMIZATION (W-23754187): emit the SAME production-format URL for the canonical link
    // (via `seoMeta`/`meta` below), `og:url`, and the Product JSON-LD `url`, so all three agree with
    // production's indexed `/{slug}/{id}.html`. Previously `pageUrl` used the raw request path, which
    // left og:url / the schema `url` pointing at the non-canonical `/product/{id}` while the canonical
    // pointed at `.html` — a self-contradiction on the native route. Fall back to the request path
    // (with content-affecting params) when the canonical slug can't be derived.
    const pageUrl = canonicalProductPath
        ? buildCanonicalUrl(publicOrigin, canonicalProductPath, '')
        : buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);

    // RGBARRY-CUSTOMIZATION (production SEO audit): catalog descriptions can contain authored HTML.
    // Resolve them once on the server, with the same localized site-name source used by SeoMeta.
    const { t } = getTranslation(context);
    const seoDescription = buildProductSeoDescription(
        product.name,
        t('common:defaultSiteName'),
        product.pageDescription,
        product.shortDescription
    );

    // RGBARRY-CUSTOMIZATION (W-23754187): canonical + hreflang descriptors, built from the same
    // `canonicalProductPath`. Emitted through this route's `meta` export (below), which by React
    // Router v7 semantics REPLACES root's meta rather than appending, so there is no duplicate tag.
    const { site, locale } = siteCtx;
    const seoMeta = buildSeoMetaDescriptors({
        site,
        locale,
        appConfig: getConfig(context),
        origin: publicOrigin,
        location: canonicalProductPath
            ? { pathname: canonicalProductPath, search: '' }
            : { pathname: requestUrl.pathname, search: requestUrl.search },
    });

    // Generate product schema in loader (server-side) for SEO.
    // Wrapped in a Promise so it can be rendered through Suspense without blocking
    // the loader response. The inner try/catch logs synchronous schema-generation failures
    // (this is local computation, not a SCAPI call — `fetchProductById` already logs SCAPI
    // errors at the API layer) so we keep visibility on rare malformed-input bugs.
    // Render-time failures degrade silently via the route-level <Await errorElement={null}>.
    const productSchemaPromise: Promise<ReturnType<typeof generateProductSchema> | null> = Promise.resolve().then(
        () => {
            try {
                // RGBARRY-CUSTOMIZATION (W-23754187): use the same canonical public URL as og:url and
                // `<link rel="canonical">` so the structured-data `url` matches the declared canonical
                // (and never exposes the internal Lambda/CDN origin).
                return generateProductSchema(product, pageUrl);
            } catch (error) {
                logger.error('Error generating product schema in loader', { error });
                return null;
            }
        }
    );

    // Category context for Page Designer PDP content, sourced from the primary_category
    // expansion (falls back to the flat primaryCategoryId when the expansion is absent).
    const primaryCategoryId = product.primaryCategory?.id ?? product.primaryCategoryId;
    const page = fetchPageWithComponentData(args, {
        aspectType: 'pdp',
        productId: productLookupId,
        ...(primaryCategoryId ? { categoryId: primaryCategoryId } : {}),
    });

    // RGBARRY-CUSTOMIZATION (W-23875749): breadcrumb chain's production SEO slug paths, read straight
    // off the ENRICHED PRODUCT RESPONSE — no extra fetch (Kevin He review, PR #107). The product hook
    // stamps `c_seoUrl` onto each node of the embedded `primaryCategory` chain (leaf + parentCategoryTree)
    // the loader already expanded above, so the slugs ride the SAME single product GET (OOTB-aligned with
    // the mega-menu's `c_seoUrl` convention; verified serializing on bjgk-006 under the primary_category
    // expand). Any crumb without a slug stays unmapped and CategoryBreadcrumbs' useCategoryHref falls
    // back to /category/:id (graceful, never broken).
    const categoryUrls: CategoryUrlMap = collectProductBreadcrumbCategoryUrls(product);

    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    // RGBARRY-CUSTOMIZATION: the PDP buy-box rating is sourced SOLELY from the product's
    // Bazaarvoice catalog attributes (c_bvAverageRating / c_bvReviewCount). When both are
    // present and positive the real rating is shown; otherwise the summary is zeroed so
    // ProductRatingSummary renders nothing — we never fall back to the template's mock
    // reviews. Shared with the cart-item modal via buildBvReviewsSummary.
    const reviewsSummary: ReviewsSummaryData = buildBvReviewsSummary(product);
    // The reviews section itself is Bazaarvoice's hosted widget, so no OOTB mock review
    // list is seeded. A non-null (zeroed) summary above keeps the provider from client-fetching
    // the mock summary.
    const reviewsList = null;
    const writeReviewForm = getWriteReviewForm(productLookupId);
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS

    return {
        product,
        // RGBARRY-CUSTOMIZATION (W-23875749): synchronous — the breadcrumb slug paths are read straight
        // off the product's hook-enriched embedded `primaryCategory` chain (no extra fetch), so links
        // carry prod SEO slugs in the SSR HTML (SEO) and degrade to {} (id-route fallback) on any miss.
        categoryUrls,
        page,
        pageKey: productId,
        pageUrl,
        seoDescription,
        // RGBARRY-CUSTOMIZATION (W-23754187): production-format canonical + hreflang for this route's `meta`.
        seoMeta,
        productSchema: productSchemaPromise,
        // @sfdc-extension-block-start SFDC_EXT_BNPL
        bnplMessage: getBuyNowPayLaterMessage(productLookupId),
        bnplLearnMore: getBuyNowPayLaterLearnMore(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_BNPL
        // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
        reviewsSummary,
        reviewsList,
        writeReviewForm,
        // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
        // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
        returnsWarranty: getReturnsAndWarranty(productLookupId),
        pdpCollapsibles: Promise.all(
            resolvePdpSections(product).map((section) =>
                pdpSectionApi[section.apiMethod](productLookupId).catch(() => null)
            )
        ),
        // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
        // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
        estimatedDelivery: getEstimatedDelivery(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
        aPlusContent: (async () => {
            const prod = product as unknown as Record<string, unknown>;
            const ownAssetId =
                typeof prod.c_aPlusContentAssetID === 'string' && prod.c_aPlusContentAssetID.trim()
                    ? prod.c_aPlusContentAssetID
                    : '';
            // Fall back to the master's asset ID only when the variant lacks its own
            const assetId =
                ownAssetId ||
                (((masterProduct as unknown as Record<string, unknown> | null)?.c_aPlusContentAssetID as
                    | string
                    | undefined) ??
                    '');
            // RGBARRY-CUSTOMIZATION: send only A+ rendering data to the browser; omit unrelated asset metadata.
            const asset = await fetchContentAssetHtml(context, assetId, { deferImages: true });
            if (!asset) return null;
            return asset.aPlusCarousel ? { aPlusCarousel: asset.aPlusCarousel } : { html: asset.html };
        })(),
        // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config from site preferences
        fibblConfig: await getFibblConfig(context),
        // RGBARRY-CUSTOMIZATION: kick off the below-the-fold recommendation fetches here and return them
        // as UNRESOLVED promises so they never block the loader (rule #3). The product is passed as the
        // Einstein anchor, mirroring the client path's `productFromContext`. Consumed via
        // <DeferredProductRecommendations> (Suspense/Await) in the view.
        pdpCompleteSetRecs: fetchProductRecommendations(
            { context, request },
            {
                name: EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET,
                products: [product],
                limit: 8,
                ...(currency ? { currency } : {}),
            }
        ),
        pdpRecentlyViewedRecs: fetchProductRecommendations(
            { context, request },
            {
                name: EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED,
                products: [product],
                limit: 8,
                ...(currency ? { currency } : {}),
            }
        ),
        // END RGBARRY-CUSTOMIZATION
        // RGBARRY-CUSTOMIZATION: PDP color swatch grouping by sale status
        isSaleByColorValue,
        // END RGBARRY-CUSTOMIZATION
    };
}

// Gates both the navigation axis (skip client-only param changes like color/size) and the action axis (skip the
// expensive loader re-run after cart/wishlist/account mutations that touch nothing the loader reads). The policy and
// its denylist live in the shared module so the rationale is documented and unit-tested in one place.
// RGBARRY-CUSTOMIZATION: `analytics.routeType` classifies this route for the GTM data layer's
// page-view event (see src/analytics/route-analytics.ts). Route-based, so it survives the legacy
// `/<slug>/<id>.html` PDP URL shape that no path-prefix blocklist could match. This handle is
// re-exported by the `.html` PDP route, so that URL is classified identically.
export const handle = {
    ui: { main: { backgroundClass: 'bg-background' } },
    analytics: { routeType: 'product' },
};

export const shouldRevalidate = shouldRevalidateProduct;

// RGBARRY-CUSTOMIZATION (W-23754187): override root's default canonical/hreflang with the PDP's
// production-format descriptors computed in the loader. React Router v7 uses the nearest leaf `meta`
// export in place of ancestor meta (it replaces, not merges), so this cleanly swaps root's
// `/product/{id}` canonical for `/{slug}/{id}.html` with no duplicate tags. Re-exported by the
// legacy `/{slug}/{id}.html` route so both PDP URL shapes emit the identical canonical.
export const meta: Route.MetaFunction = ({ loaderData }) => loaderData?.seoMeta ?? [];

// RGBARRY-CUSTOMIZATION: counts <li> tags in the shortDescription HTML so the description accordion
// can switch to a two-column layout for long bullet lists; see usage below.
function countListItems(html: string): number {
    return (html.match(/<li[\s>]/gi) ?? []).length;
}

function ProductContent({
    product,
    url,
    seoDescription,
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary,
    reviewsList,
    writeReviewForm,
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise,
    pdpCollapsiblesPromise,
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    aPlusContentPromise,
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig,
    // END RGBARRY-CUSTOMIZATION
    // RGBARRY-CUSTOMIZATION: PDP color swatch grouping by sale status
    isSaleByColorValue,
    // END RGBARRY-CUSTOMIZATION
}: {
    product: ShopperProducts.schemas['Product'];
    url: string;
    seoDescription?: string;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData> | null;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise: Promise<ReturnsAndWarrantyData>;
    pdpCollapsiblesPromise: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    // RGBARRY-CUSTOMIZATION: A+ content supports sanitized HTML and a structured carousel model.
    aPlusContentPromise: Promise<APlusContentAsset | null>;
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig: FibblConfig;
    // END RGBARRY-CUSTOMIZATION
    // RGBARRY-CUSTOMIZATION: PDP color swatch grouping by sale status
    isSaleByColorValue: Record<string, boolean>;
    // END RGBARRY-CUSTOMIZATION
}) {
    // RGBARRY-CUSTOMIZATION: suppress the preliminary master event when a selected variant is
    // resolving, and treat subsequent swatch variants as the same logical PDP view.
    useProductViewTracking(product);

    const primaryImage =
        product.imageGroups?.find((g) => g.viewType === 'large')?.images?.[0]?.link ??
        product.imageGroups?.[0]?.images?.[0]?.link;

    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);
    return (
        <ProductProvider product={product}>
            {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            <ProductContentDataProvider
                product={product}
                returnsWarrantyPromise={returnsWarrantyPromise}
                pdpCollapsiblesPromise={pdpCollapsiblesPromise}>
                {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
                {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                {/* Provider wraps ProductView so the in-page rating summary shares state
                        with the customer reviews accordion (expand/jump-to coordination). */}
                <ProductReviewsProvider summary={reviewsSummary} reviewsListPromise={reviewsList}>
                    <WriteReviewFormProvider writeReviewFormPromise={writeReviewForm}>
                        {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                        <SeoMeta
                            title={product.name}
                            description={seoDescription}
                            openGraph={{
                                type: 'product',
                                url,
                                image: primaryImage,
                            }}
                        />
                        <div className="space-y-8">
                            {isProductASet || isProductABundle ? (
                                <>
                                    <ProductView
                                        product={product}
                                        fibblConfig={fibblConfig}
                                        isSaleByColorValue={isSaleByColorValue}
                                    />
                                    <ChildProducts parentProduct={product} />
                                </>
                            ) : (
                                <ProductView
                                    product={product}
                                    fibblConfig={fibblConfig}
                                    isSaleByColorValue={isSaleByColorValue}
                                />
                            )}

                            {/* RGBARRY-CUSTOMIZATION: Description accordion (shortDescription HTML), open by default */}
                            {product.shortDescription && (
                                <Accordion
                                    type="single"
                                    collapsible
                                    defaultValue="features"
                                    className="w-full border-t border-[#e8e4e0]">
                                    <AccordionItem value="features" className="border-b border-[#e8e4e0]">
                                        <AccordionTrigger className="text-sm font-semibold tracking-wide uppercase py-4 hover:no-underline">
                                            Description
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div
                                                className={cn(
                                                    'text-sm text-[#3a3a3a] pb-2 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-4 [&_li]:leading-relaxed [&_li]:break-inside-avoid',
                                                    // RGBARRY-CUSTOMIZATION: long bullet lists wrap into two
                                                    // columns on desktop so they don't dwarf the product images
                                                    countListItems(product.shortDescription) > 6 &&
                                                        'md:columns-2 md:gap-x-8'
                                                )}
                                                // oxlint-disable-next-line react/no-danger -- product shortDescription is authored HTML from Commerce Cloud
                                                dangerouslySetInnerHTML={{
                                                    __html: product.shortDescription,
                                                }}
                                            />
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            )}

                            {/* RGBARRY-CUSTOMIZATION: A+ content streams below the fold; known legacy
                                carousels use the scoped renderer and all other assets retain sanitized HTML. */}
                            <Suspense fallback={null}>
                                <Await resolve={aPlusContentPromise} errorElement={null}>
                                    {(asset) =>
                                        asset ? (
                                            // RGBARRY-CUSTOMIZATION: remount viewport-gated A+ media when a swatch changes the selected variant, even when variants share one asset.
                                            <APlusContent key={product.id} asset={asset} />
                                        ) : null
                                    }
                                </Await>
                            </Suspense>
                            {/* END RGBARRY-CUSTOMIZATION */}

                            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                            <UITarget targetId="sfcc.pdp.reviews.section" />
                            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                            <UITarget targetId="sfcc.pdp.reviews.qna" />
                        </div>
                        {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                    </WriteReviewFormProvider>
                </ProductReviewsProvider>
                {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            </ProductContentDataProvider>
            {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
        </ProductProvider>
    );
}

/**
 * Product detail shell that composes the page layout with granular Suspense boundaries.
 * Regions render independently (they manage their own async via Suspense/Await),
 * while the core product content renders synchronously from the resolved loader data.
 */
function ProductDetailView({ loaderData }: { loaderData: ProductPageData }) {
    const { t } = useTranslation('product');
    // RGBARRY-CUSTOMIZATION: pin the deferred recommendation promises so a non-navigating revalidation
    // doesn't hand <Suspense> a fresh promise reference and re-suspend the resolved carousels.
    const [completeSetRecs] = useState(() => loaderData.pdpCompleteSetRecs);
    const [recentlyViewedRecs] = useState(() => loaderData.pdpRecentlyViewedRecs);
    const content = (
        <div className="min-h-screen bg-background">
            <div className="section-container pb-4 lg:pb-8">
                {/* Promo Content Region - Promotional content above main product */}
                <Region className="mb-8" page={loaderData.page} regionId="promoContent" />

                {/* Category breadcrumbs — sourced from the product's primary_category
                    expansion, so they render synchronously with the product (in SSR HTML for
                    SEO). Absent a primary category, the row renders nothing. */}
                {loaderData.product.primaryCategory ? (
                    <CategoryHrefProvider value={loaderData.categoryUrls}>
                        <CategoryBreadcrumbs category={loaderData.product.primaryCategory} />
                    </CategoryHrefProvider>
                ) : null}

                {/* Main Product Content — product is resolved synchronously by the loader */}
                <ProductContent
                    product={loaderData.product}
                    url={loaderData.pageUrl}
                    seoDescription={loaderData.seoDescription}
                    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
                    reviewsSummary={loaderData.reviewsSummary}
                    reviewsList={loaderData.reviewsList}
                    writeReviewForm={loaderData.writeReviewForm}
                    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
                    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
                    returnsWarrantyPromise={loaderData.returnsWarranty}
                    pdpCollapsiblesPromise={loaderData.pdpCollapsibles}
                    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
                    aPlusContentPromise={loaderData.aPlusContent}
                    fibblConfig={loaderData.fibblConfig}
                    isSaleByColorValue={loaderData.isSaleByColorValue}
                />

                {/* Engagement Content Region - PD editorial content when present */}
                <Region className="mt-16" page={loaderData.page} regionId="engagementContent" />

                {/* RGBARRY-CUSTOMIZATION: Einstein recommendations — always-on, not gated behind PD errorElement.
                    Server-deferred: data is fetched in the loader and streamed via Suspense/Await once the
                    browser is idle, instead of a post-hydration client fetch. Skeleton fallbacks reserve height
                    so the block doesn't shift the footer when the carousels resolve (CLS). */}
                <div className="mt-16 space-y-16">
                    <DeferredProductRecommendations
                        recommenderName={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                        recommenderTitle={t('recommendations.youMightAlsoLike')}
                        data={completeSetRecs}
                        className="max-w-none px-0"
                        fallback={
                            <ProductRecommendationSkeleton
                                title={t('recommendations.youMightAlsoLike')}
                                className="max-w-none px-0"
                            />
                        }
                    />
                    <DeferredProductRecommendations
                        recommenderName={EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED}
                        recommenderTitle={t('recommendations.recentlyViewed')}
                        data={recentlyViewedRecs}
                        className="max-w-none px-0"
                        fallback={
                            <ProductRecommendationSkeleton
                                title={t('recommendations.recentlyViewed')}
                                className="max-w-none px-0"
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );

    let finalContent = content;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    // RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP
    // finalContent = <PickupProvider>{finalContent}</PickupProvider>;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    finalContent = (
        <BnplProvider messagePromise={loaderData.bnplMessage} learnMorePromise={loaderData.bnplLearnMore}>
            {finalContent}
        </BnplProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    finalContent = (
        <ShippingDeliveryProvider estimatedDeliveryPromise={loaderData.estimatedDelivery}>
            {finalContent}
        </ShippingDeliveryProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

    return finalContent;
}

/**
 * Product page component that displays a product with its details and category breadcrumbs.
 * The page key ensures React only remounts when navigating to a different product, not variants.
 * @returns JSX element representing the product page with Suspense boundary
 */
export default function ProductPage({ loaderData }: { loaderData: ProductPageData }) {
    // Use pageKey from loaderData to force remount only when productId changes
    // This prevents showing skeleton when switching variants (pid parameter)
    const pageKey = loaderData.pageKey;

    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;

    return (
        <Fragment key={pageKey}>
            <ProductDetailView loaderData={loaderData} />

            {/* Product JSON-LD Schema for SEO - render after page content so it appears at end of body flow.
                JSON-LD is non-critical: errorElement renders nothing so a schema-generation failure
                silently degrades to no <script> tag. */}
            <Suspense fallback={null}>
                <Await resolve={loaderData.productSchema} errorElement={null}>
                    {(productSchema) =>
                        productSchema ? <JsonLd data={productSchema} id="product-schema" nonce={nonce} /> : null
                    }
                </Await>
            </Suspense>
        </Fragment>
    );
}
